import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '../components/ui';
import emailjs from '@emailjs/browser';

export default function CandidatePage() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const alertForm = useRef();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const API_KEY = 'mFFkH1qjhhW4YK0QCUVA9EkPdd93iT_WFHMC3wRHvMmuk692MIBJuEl_iZXizncLcRdFGS3L8CDz6CzOzLqPHF8xNzU0NDI3Nzk4Onw6cHJvZHVjdGlvbg==';

  useEffect(() => {
    // Skip the live jobs API while react-snap pre-renders, so the build never waits on the network.
    if (/ReactSnap/i.test(navigator.userAgent)) return;

    const fetchJobs = async () => {
      try {
        const response = await fetch('https://api.recruitcrm.io/v1/jobs', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${API_KEY}`,
          },
        });
        const data = await response.json();
        if (data && data.data) {
          setJobs(data.data);
          setFilteredJobs(data.data);
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  useEffect(() => {
    let results = jobs;

    if (searchTerm.trim() !== '') {
      results = results.filter((job) =>
        job.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedLocation) {
      results = results.filter(
        (job) =>
          job.city &&
          job.city.toLowerCase().includes(selectedLocation.toLowerCase())
      );
    }

    if (selectedCategory) {
      results = results.filter(
        (job) =>
          job.job_category &&
          job.job_category.toLowerCase().includes(selectedCategory.toLowerCase())
      );
    }

    setFilteredJobs(results);
  }, [searchTerm, selectedLocation, selectedCategory, jobs]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    emailjs
      .sendForm(
        'service_58jl8rh',
        'template_lnkxw49',
        alertForm.current,
        'WZ8UBSqUYOkPtkkuy'
      )
      .then(
        () => {
          setIsSubmitted(true);
          setSubmitting(false);
        },
        () => {
          setError('Something went wrong. Please try again.');
          setSubmitting(false);
        }
      );
  };

  const uniqueLocations = [...new Set(jobs.map((j) => j.city).filter(Boolean))];
  const uniqueCategories = [...new Set(jobs.map((j) => j.job_category).filter(Boolean))];

  return (
    <>
      <Helmet>
        <title>Candidate Hub | Semiconductor Jobs at Chiparama</title>
        <meta
          name="description"
          content="Build your profile and explore semiconductor and engineering job opportunities at Chiparama and our partner companies."
        />
        <link rel="canonical" href="https://chiparama.com/candidates" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Candidate Hub | Semiconductor Jobs at Chiparama" />
        <meta
          property="og:description"
          content="Build your profile and explore semiconductor and engineering job opportunities at Chiparama and our partner companies."
        />
        <meta property="og:url" content="https://chiparama.com/candidates" />
        <meta property="og:image" content="https://chiparama.com/chiparamalogo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Candidate Hub | Semiconductor Jobs at Chiparama" />
        <meta
          name="twitter:description"
          content="Build your profile and explore semiconductor and engineering job opportunities at Chiparama and our partner companies."
        />
        <meta name="twitter:image" content="https://chiparama.com/chiparamalogo.jpg" />
      </Helmet>

      <PageHeader eyebrow="Candidate Hub" title="Explore opportunities at Chiparama and our partner companies.">
        Build your profile and apply to roles that match your skills. Enter your email below to get job
        alerts and updates. Add a mobile number if you'd also like text alerts.
      </PageHeader>

      <section className="section-tight bg-white">
        <div className="container-x max-w-3xl">
          {isSubmitted ? (
            <div role="status" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-800">
              <p className="font-semibold">Thank you. You'll receive updates about new job openings.</p>
            </div>
          ) : (
            <form ref={alertForm} method="post" onSubmit={handleSubmit} className="card !p-7 md:!p-9">
              <h2 className="text-xl font-semibold text-ink">Get job alerts</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="job_alert_email" className="field-label">Email</label>
                  <input
                    type="email"
                    id="job_alert_email"
                    name="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    className="field"
                    autoComplete="email"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="job_alert_phone" className="field-label">
                    Mobile number <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="job_alert_phone"
                    name="user_phone"
                    placeholder="(555) 555-5555"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="field"
                    autoComplete="tel"
                  />
                </div>
              </div>

              {/* Fields the shared EmailJS template expects */}
              <input type="hidden" name="user_email" value={email} />
              <input type="hidden" name="user_name" value="Job alert signup" />
              <input type="hidden" name="subject" value="Job alert signup" />
              <input type="hidden" name="message" value="Job alert signup from the Candidate Hub" />
              <input type="hidden" name="sms_consent" value={smsConsent ? 'Yes' : 'No'} />

              {/* SMS opt-in: optional, unchecked by default, form submits either way */}
              <label className="mt-5 flex items-start gap-3 text-xs leading-relaxed text-slate-500">
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand focus:ring-brand"
                />
                <span>
                  By opting in for text messages, you agree to receive job opening alerts and company
                  updates from Chiparama Inc at the number provided. Message frequency varies. Msg &amp; data
                  rates may apply. Reply STOP to unsubscribe. Reply HELP for help. View our{' '}
                  <Link to="/privacy-policy" className="link">Privacy Policy</Link> and{' '}
                  <Link to="/terms-and-conditions" className="link">Terms &amp; Conditions</Link> for more
                  information. Consent to receive SMS messages is not a condition of purchasing any goods
                  or services.
                </span>
              </label>

              <button type="submit" disabled={submitting} className="btn-primary mt-6 w-full py-3.5 sm:w-auto">
                {submitting ? 'Sending...' : 'Notify me'}
              </button>
              <p className="mt-4 text-xs text-slate-500">
                By submitting, you agree to receive job-alert emails from Chiparama. See our{' '}
                <Link to="/privacy-policy" className="link">Privacy Policy</Link>.
              </p>
              {error && <p role="alert" className="mt-4 text-sm text-red-600">{error}</p>}
            </form>
          )}
        </div>
      </section>

      <section className="section-tight bg-paper">
        <div className="container-x">
          <div className="mb-10 grid gap-6 md:grid-cols-4">
            <h2 className="text-2xl font-semibold text-ink md:col-span-4">How selection works</h2>
            {['Domain screen', 'Client alignment', 'Interview', 'Engagement'].map((step, i) => (
              <div key={step} className="card !p-5">
                <span className="font-mono text-xs text-brand">0{i + 1}</span>
                <p className="mt-2 font-medium text-ink">{step}</p>
              </div>
            ))}
          </div>

          <h2 className="mt-16 text-2xl font-semibold text-ink">Open positions</h2>

          {!loading && jobs.length > 0 && (
            <div className="mt-6 grid gap-3 md:grid-cols-[2fr_1fr_1fr]">
              <input
                type="search"
                aria-label="Search jobs"
                placeholder="Search jobs"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="field"
              />
              <select aria-label="Location" value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} className="field">
                <option value="">All locations</option>
                {uniqueLocations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
              <select aria-label="Category" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="field">
                <option value="">All categories</option>
                {uniqueCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mt-8">
            {loading ? (
              <p className="text-slate-500">Loading available jobs...</p>
            ) : jobs.length === 0 ? (
              <div className="card text-slate-600">
                No open positions right now. Join our specialist network above to hear about new programs first.
              </div>
            ) : filteredJobs.length === 0 ? (
              <p className="text-slate-500">No jobs match your filters. Try adjusting your search.</p>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {filteredJobs.map((job) => (
                  <article key={job.id} className="card card-hover flex flex-col">
                    <h3 className="text-lg font-semibold text-ink">{job.name}</h3>
                    <p className="mt-2 font-mono text-xs uppercase tracking-wider text-slate-500">
                      {job.targetcompanies?.[0]?.name || 'Chiparama'} · {job.city || 'Location not specified'}
                    </p>
                    <p className="mt-4 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-600">
                      {job.note_for_candidates || 'View details for more information.'}
                    </p>
                    {job.application_form_url ? (
                      <a href={job.application_form_url} target="_blank" rel="noopener noreferrer" className="btn-secondary mt-6">
                        Apply now
                      </a>
                    ) : (
                      <a
                        href={`mailto:careers@chiparama.com?subject=${encodeURIComponent('Application: ' + job.name)}`}
                        className="btn-secondary mt-6"
                      >
                        Apply by email
                      </a>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
